import { CloseOutlined } from "@mui/icons-material";
import { Box, DialogContent, Drawer, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { RootState } from "../../../../../store/store";
import {
  useCallback,
  useState,
  SetStateAction,
  Dispatch,
  useEffect,
  useMemo,
} from "react";
import AddDriverFormStyles from "../../VehiclesAnDrivers/Drivers/AddDriverForm/AddDriverFormStyles";
import MessageDialog from "../../../../../components/MessageDialog/MessageDialog";
import {
  IAsyncOptionFn,
  IDynamicFormFieldsJson,
} from "../../../../../models/user/DynamicForm/DynamicForm";
import {
  AddVehicleFinanceCompany,
  GetAddLienholderFormJson,
} from "../LienholderActions";
import DynamicForm from "../../../../../common/DynamicForm/DynamicForm";
import { GetPolicyRisks } from "../../../../../store/user/policyDetails/policyDetailsActions";
import { ILienholderPolicyChangeRequestParams } from "../../../../../models/user/PolicyChange/PolicyChange";
import { setAddLienholderFormFieldsJson } from "../LienholderSlice";
import { getCookie } from "../../../../../utils/Utility";
import { setCalledReducerType } from "../../../../../store/common/CommonSlice";

interface IProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  changePolicyId?: string;
  changePolicyLobCode?: string;
}
const AddLienHolderForm: React.FC<IProps> = ({
  open,
  setOpen,
  changePolicyId,
  changePolicyLobCode,
}) => {
  const dispatch = useAppDispatch();

  let { policyId, lobCode } = useParams();

  policyId = policyId ? policyId : changePolicyId;
  lobCode = lobCode ? lobCode : changePolicyLobCode;

  const carrier = getCookie("carrier");
  const system = getCookie("system");

  const { policyVehicles } = useAppSelector(
    (store: RootState) => store.policyDetails
  );

  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);

  const { policyDetailsHeader } = useAppSelector(
    (state: RootState) => state.policyDetails
  );

  useEffect(() => {
    if (policyId && carrier && system && lobCode) {
      dispatch(GetPolicyRisks({ policyId, carrier, system, lobCode }));
    }
  }, [policyId, carrier, system, lobCode, dispatch]);

  const { t } = useTranslation();

  const { addLienholderFormFieldsJson } = useAppSelector(
    (state: RootState) => state.lienholder
  );

  const { isSuccess, isError, calledReducerType } = useAppSelector(
    (state: RootState) => state.common
  );
  const [formFieldsJson, setFormFieldsJson] =
    useState<IDynamicFormFieldsJson | null>(null);

  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  const asyncFunctions: { [key: string]: IAsyncOptionFn } = useMemo(
    () => ({
      getHasInterestInOptions: async () => {
        try {
          const options = policyVehicles?.map((vehicle) => {
            return {
              name: "hasInterestIn",
              code: `${vehicle?.manufactureYr}-${vehicle?.make}-${vehicle?.model}-${vehicle.vehicleType}-${vehicle.vin}`,
              label: `${vehicle?.manufactureYr}-${vehicle?.make}-${vehicle?.model}`,
            };
          });
          return options;
        } catch (error) {
          return [];
        }
      },
    }),
    [dispatch, policyVehicles]
  );

  const handleClose = useCallback(() => {
    dispatch(setAddLienholderFormFieldsJson(null));
    setOpen(false);
  }, [setOpen]);

  const handleMessageDialogClose = useCallback(() => {
    setMessageDialogOpen(false);
    setCalledReducerType(null);
    handleClose();
  }, []);

  const handleAddLienHolderFormOnSubmit = (formValues: {
    [key: string]: any;
  }) => {
    if (
      policyDetailsHeader &&
      policyDetailsHeader?.carrierPolicyNumber &&
      policyVehicles &&
      userDetails &&
      userDetails.customerId &&
      policyId &&
      system &&
      carrier &&
      lobCode
    ) {
      const formattedChangeDate = new Date(formValues.effectiveDate)
        .toISOString()
        .split("T")[0];
      const requestParams: ILienholderPolicyChangeRequestParams = {
        carrierPolicyNo: policyDetailsHeader?.carrierPolicyNumber,
        changeDt: formattedChangeDate,
        customerId: userDetails?.customerId,
        policyRef: policyId,
        carrier: carrier,
        system: system,
        lobCode: lobCode,
        detailDescription: "Adding a Lienholder to the  policy from UI",
        additionalInterestDetails: {
          interestName: formValues.name,
          loanNumber: formValues.loanNumber,
          address: null,
          phoneInfo: formValues.contact,
          interestTypeCd: formValues.interestType || "Leinholder/Loss Payee",
          status: "Active",
        },
        vehicle: {
          make: formValues?.hasInterestIn?.split("-")[1],
          manufactureYr: formValues?.hasInterestIn?.split("-")[0],
          model: formValues?.hasInterestIn?.split("-")[2],
          vehicleType: formValues?.hasInterestIn?.split("-")[3],
          vin: formValues?.hasInterestIn?.split("-")[4],
          address: null,
        },
      };

      dispatch(AddVehicleFinanceCompany(requestParams));
    }
  };

  useEffect(() => {
    if (open) {
      dispatch(GetAddLienholderFormJson());
      dispatch(setCalledReducerType(null));
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (addLienholderFormFieldsJson) {
      setFormFieldsJson(addLienholderFormFieldsJson);
    }
  }, [addLienholderFormFieldsJson]);
  useEffect(() => {
    if (
      (isSuccess || isError) &&
      calledReducerType === "policyChange/AddVehicleFinanceCompany"
    ) {
      setMessageDialogOpen(true);
      dispatch(setCalledReducerType(null));
    }
  }, [isSuccess, isError, calledReducerType, handleClose, setOpen]);

  return (
    <>
      <MessageDialog
        handleClose={handleMessageDialogClose}
        open={messageDialogOpen}
      />
      <DialogContent
        id="add_lienholder_form_content"
        data-test-id="add_lienholder_form_content"
        aria-label="Add lienholder form content"
      >
        {formFieldsJson ? (
          <DynamicForm
            formFieldsJson={formFieldsJson}
            onFormSubmit={handleAddLienHolderFormOnSubmit}
            asyncOptionsFunctions={asyncFunctions}
          />
        ) : (
          <></>
        )}
      </DialogContent>
      {/* </Drawer> */}
      <MessageDialog
        open={messageDialogOpen}
        handleClose={handleMessageDialogClose}
      />
    </>
  );
};

export default AddLienHolderForm;
